import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AreaOption } from 'app/core/cma/cma.models';
import { MapToolsStore } from 'app/core/dashboard-page/map.model';
import { Polygon } from 'app/shared/ll/models/geometry';
import { compareFn } from 'app/shared/models';
import { EventsService } from 'app/shared/service/events.service';
import { Column } from 'app/shared/table/comparables-table.component';
import { NotificationsService } from 'app/shared/toast';
import { ModalsService } from 'app/shared/ui/modals/modals.service';
import { Option } from 'app/shared/ui/select-box/option.model';
import { EMPTY, map, mergeMap, pipe, switchMap, tap } from 'rxjs';
import { AreasApi } from './areas-api.service';
import { Area, AreaItem, DrawnPolygon } from './areas.model';


type State = {
    loaded: boolean;
    areas: AreaItem[];
    polygons: DrawnPolygon[],
    area: Partial<Area>;
    options: {
        subAreas: AreaOption[],
        broker: {
            brokers: Option[];
            propertyTypes: Option[];
        };
        mls: {
            statuses: Option[];
            propertyClasses: Option[];
            propertyTypes: Option[];
            zonings: Option[];
        };
    };
    infoOrderBy: string | null;
    infoOrderReverse: boolean | null;
    brokerOrderBy: string | null;
    brokerOrderReverse: boolean | null;
    mlsColumns: Column[] | null;
    mlsOrderBy: string | null;
    mlsOrderReverse: boolean | null;
    // used for keeping track where to return when drawing on the map
    workflow: 'areas' | 'watched-area' | 'watched-areas';
}


const logError = <T>(error: T) => console.error(error);


export const AreasStore = signalStore(
    { providedIn: 'root' },

    withState<State>({
        loaded: false,
        areas: [],
        polygons: [],
        area: {},
        options: {
            subAreas: [],
            broker: {
                brokers: [],
                propertyTypes: [],
            },
            mls: {
                statuses: [],
                propertyClasses: [],
                propertyTypes: [],
                zonings: [],
            },
        },
        infoOrderBy: null,
        infoOrderReverse: null,
        brokerOrderBy: null,
        brokerOrderReverse: null,
        mlsColumns: null,
        mlsOrderBy: null,
        mlsOrderReverse: null,
        workflow: 'areas',
    }),

    withMethods(store => {

        const api = inject(AreasApi);

        const router = inject(Router);

        const ns = inject(NotificationsService);

        const modalsService = inject(ModalsService);

        const mapToolsStore = inject(MapToolsStore);

        const eventsService = inject(EventsService);

        const patchAreaItem = (id: string, updateFn: (area: AreaItem) => Partial<AreaItem>) =>
            patchState(store, { areas: store.areas().map(a => a.id === id ? { ...a, ...updateFn(a) } : a) });

        const drawBoundary = (geometry?: Polygon[], areaId?: string) => {
            router.navigate(['/dashboard']);
            if (geometry && geometry.length > 0) {
                mapToolsStore.transition('drawBoundary');
                geometry = geometry.map((polygon, id) => ({ id, ...polygon }));
                patchState(store, {
                    area: areaId && store.area()?.id === areaId ? { ...store.area(), geometry } : { geometry },
                    polygons: geometry.map((p, id) => ({ ...p, id })),
                });
            } else {
                mapToolsStore.transition('drawBoundaryPolygon');
            }
        };

        return {
            selectArea: rxMethod<void>(
                pipe(
                    switchMap(() => api.create({ type: 'selection' })),
                    tapResponse({
                        next: areaId => {
                            mapToolsStore.transition('cancel');
                            patchState(store, { polygons: [], workflow: 'areas' });
                            router.navigate(['/dashboard', 'areas', areaId, 'select-area']);
                        },
                        error: logError,
                    }),
                )
            ),

            drawBoundary(geometry?: Polygon[], areaId?: string) {
                // clear area if ids do not match
                patchState(store, { workflow: 'areas', area: store.area()?.id !== areaId ? { id: areaId } : store.area() });
                drawBoundary(geometry, areaId);
            },

            addPolygon(polygon: DrawnPolygon) {
                patchState(store, { polygons: [...store.polygons(), polygon] });
            },

            editBoundary(id: number, polygon: DrawnPolygon) {
                patchState(store, { polygons: store.polygons().map(p => p.id === id ? polygon : p) });
            },

            completeDrawBoundary: rxMethod<void>(
                pipe(
                    switchMap(() => {
                        const geometry = store.polygons();
                        const areaId = store.area().id;
                        if (areaId) {
                            return api.update({ id: areaId, type: 'geometry', geometry }).pipe(map(() => areaId));
                        } else {
                            return api.create({ type: 'geometry', geometry });
                        }
                    }),
                    tapResponse({
                        next: (areaId: string) => {
                            patchState(store, { polygons: [] });
                            mapToolsStore.transition('complete');
                            router.navigate(['/dashboard', 'areas', areaId, 'notification-types']);
                        },
                        error: logError,
                    }),
                )
            ),

            cancelAreas() {
                if (store.workflow() === 'watched-areas') {
                    router.navigate(['/dashboard', 'watched-areas']);
                } else if (store.workflow() === 'watched-area') {
                    router.navigate(['/dashboard', 'watched-area', store.area().id, 'criteria']);
                } else {
                    router.navigate(['/dashboard']);
                }
                patchState(store, { polygons: [] });
                mapToolsStore.transition('cancel');
            },

            clearAreas() {
                patchState(store, { polygons: [] });
                mapToolsStore.transition('clearAreas');
            },

            adjust(area: AreaItem, workflow: 'watched-area' | 'watched-areas') {
                if (area.type === 'geometry') {
                    patchState(store, { area, workflow });
                    eventsService.fitBounds.next(area.geometry.flatMap(g => g.coordinates[0]));
                    drawBoundary(area.geometry, area.id);
                } else {
                    router.navigate(['/dashboard', 'areas', area.id, 'select-area']);
                }
            },

            setArea(area: Area, options: State['options']) {
                patchState(store, { area, options });
            },

            updateArea: rxMethod<Partial<Area>>(
                pipe(
                    tap((area: Partial<Area>) => {
                        patchState(store, { area: { ...store.area(), ...area } });
                    }),
                    mergeMap(area => {
                        if (area.status === 'NEW') {
                            return api.update(store.area());
                        }
                        return EMPTY;
                    })
                )
            ),

            save: rxMethod<void>(
                pipe(
                    mergeMap(() => api.save(store.area())),
                    tapResponse({
                        next: () => {
                            const areaId = store.area().id;
                            patchState(store, { area: {}, loaded: false });
                            router.navigate(['/dashboard', 'watched-area', areaId, 'criteria']);
                        },
                        error: logError,
                    }),
                )
            ),

            loadAreas: rxMethod<void>(
                pipe(
                    tap(() => patchState(store, { loaded: false })),
                    switchMap(() => api.loadAreas()),
                    tapResponse({
                        next: (areas: AreaItem[]) => {
                            patchState(store, { loaded: true, areas: areas.map(a => ({ ...a, historyLoaded: false })) });
                        },
                        error: logError,
                    }),
                )
            ),

            toggleNotif: rxMethod<{ areaId: string; enabled: boolean }>(
                pipe(
                    mergeMap(({ areaId, enabled }) => {
                        if (enabled) {
                            router.navigate(['/dashboard', 'areas', areaId, 'notification-types']);
                            return EMPTY;
                        } else {
                            return api.update({ id: areaId, notifEnabled: false }).pipe(
                                tapResponse({
                                    next: response => {
                                        patchAreaItem(areaId, () => ({ ...response.payload, notifEnabled: false }));
                                    },
                                    error: logError,
                                }),
                            );
                        }
                    })
                )
            ),

            rename: rxMethod<{ areaId: string; name: string }>(
                pipe(
                    mergeMap(({ areaId, name }) => api.rename(areaId, name).pipe(
                        tapResponse({
                            next: response => {
                                patchAreaItem(areaId, () => ({ ...response.payload, name }));
                            },
                            error: logError,
                        })
                    )),
                )
            ),

            renew: rxMethod<string>(
                pipe(
                    mergeMap(areaId => api.renew(areaId).pipe(
                        tapResponse({
                            next: response => {
                                ns.success('Watched Areas', `Area ${store.areas().find(a => a.id === areaId)?.name} was renewed`);
                                patchAreaItem(areaId, () => response.payload);
                            },
                            error: logError,
                        })
                    )),
                )
            ),

            deleteArea: rxMethod<string>(
                pipe(
                    mergeMap(areaId => api.deleteArea(areaId).pipe(
                        tapResponse({
                            next: () => {
                                ns.success('Watched Areas', `Area ${store.areas().find(a => a.id === areaId)?.name} was successfully deleted`);
                                patchState(store, { areas: store.areas().filter(a => a.id !== areaId) });
                            },
                            error: logError,
                        }),
                    ))
                )
            ),

            loadAreaHistory: rxMethod<string>(
                pipe(
                    mergeMap(areaId => api.loadAreaHistory(areaId).pipe(
                        tapResponse({
                            next: history => {
                                patchAreaItem(areaId, () => ({ history, historyLoaded: true }));
                                patchState(store, {
                                    infoOrderBy: history.infoOrderBy,
                                    infoOrderReverse: history.infoOrderReverse,
                                    brokerOrderBy: history.brokerOrderBy,
                                    brokerOrderReverse: history.brokerOrderReverse,
                                    mlsOrderBy: history.mlsOrderBy,
                                    mlsOrderReverse: history.mlsOrderReverse,
                                    mlsColumns: history.mlsColumns,
                                });
                            },
                            error: ({ error, message }: { error: string; message: string }) => {
                                logError({ error, message });
                                let alertContent = 'There was an error while loading, please try again';
                                if (error === 'no-results') {
                                    alertContent = 'Watched Area is no longer available';
                                }
                                modalsService.alert(alertContent, 'Loading Watched Area Failed', 'danger');
                                router.navigate(['/dashboard', 'watched-areas']);
                            },
                        }),
                    )),
                )
            ),

            sortHistory(tab: 'info' | 'broker' | 'mls', orderBy: string, orderReverse: boolean) {
                patchState(store, {
                    areas: store.areas().map(a => ({
                        ...a,
                        history: a.historyLoaded ? {
                            ...a.history,
                            // @ts-ignore
                            [tab]: [...a.history[tab]].sort((a, b) => compareFn(a, b, orderBy, orderReverse)),
                        } : a.history,
                    })),
                    [tab + 'OrderBy']: orderBy,
                    [tab + 'OrderReverse']: orderReverse,
                });
            },

            setMlsColumns(mlsColumns: Column[]) {
                patchState(store, { mlsColumns });
            },

            savePreset: rxMethod<{ preset: Array<[string, boolean]> }>(
                pipe(
                    switchMap(({ preset }) => api.savePreset(preset).pipe(
                        tapResponse({
                            next: () => {
                                ns.success('Watched Areas', 'Preset successfully saved');
                            },
                            error: logError,
                        }),
                    )),
                ),
            ),
        };
    }),
);
